import { CartPanelController } from '@theme/zrzs-quick-add-cart-panel';
import {
  CartRecsController,
  fetchCrossSellProducts,
  fetchRecentProducts,
} from '@theme/zrzs-quick-add-cart-recs';

/** Drawer desktop (>=750px) — mesmo CartPanelController do overlay mobile. */
class ZRZSQuickAddCartDrawer extends HTMLElement {
  /** @type {CartPanelController} */
  cartPanelController;

  connectedCallback() {
    this.backdrop = this.querySelector('[data-zqcd-backdrop]');
    this.isOpen = false;

    this.querySelectorAll('[data-zqcd-close]').forEach(el =>
      el.addEventListener('click', this.close)
    );
    if (this.backdrop) this.backdrop.addEventListener('click', this.close);

    this.setupRecsTabs();
    this.setupRecsControllers();

    this.cartPanelController = new CartPanelController({
      root: this,
      sourceId: 'zrzs-quick-add-cart-drawer',
      isVisible: () => Boolean(this.isOpen),
      onGrauBannerClick: () =>
        document.dispatchEvent(
          new CustomEvent('zrzs:quick-add:open-solar-grau')
        ),
      onContinueShopping: () => this.close(),
    });
    this.cartPanelController.connect();

    document.addEventListener(
      'zrzs:quick-add:open-cart-desktop',
      this.handleOpenFromCartIcon
    );
    document.addEventListener('zrzs:quick-add:close-cart-desktop', this.close);
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener(
      'zrzs:quick-add:open-cart-desktop',
      this.handleOpenFromCartIcon
    );
    document.removeEventListener(
      'zrzs:quick-add:close-cart-desktop',
      this.close
    );
    document.removeEventListener('keydown', this.handleKeydown);
    if (this.cartPanelController) this.cartPanelController.disconnect();
    if (this.likedRecsController) this.likedRecsController.disconnect();
    if (this.recentRecsController) this.recentRecsController.disconnect();
  }

  /** Clique manual — só troca a aba visível; abas indisponíveis vêm `disabled` (não disparam click). */
  setupRecsTabs() {
    const tabs = this.querySelectorAll('[data-zqcd-recs-tab]');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const target = tab.dataset.zqcdRecsTab;
        tabs.forEach(other => {
          const isActive = other === tab;
          other.classList.toggle('is-active', isActive);
          other.setAttribute('aria-selected', String(isActive));
        });
        this.querySelectorAll('[data-zqcd-recs-panel]').forEach(panel => {
          panel.hidden = panel.dataset.zqcdRecsPanel !== target;
        });
      });
    });
  }

  setupRecsControllers() {
    const likedPanel = this.querySelector('[data-zqcd-recs-panel="liked"]');
    const recentPanel = this.querySelector('[data-zqcd-recs-panel="recent"]');

    this.recsAvailability = { liked: false, recent: false };

    this.likedRecsController = new CartRecsController({
      panelRoot: likedPanel,
      fetchProducts: () => fetchCrossSellProducts(4),
      clearOnCartEmpty: true,
      onAvailabilityChange: available =>
        this.updateRecsTabAvailability('liked', available),
    });
    this.recentRecsController = new CartRecsController({
      panelRoot: recentPanel,
      fetchProducts: () => fetchRecentProducts(4),
      onAvailabilityChange: available =>
        this.updateRecsTabAvailability('recent', available),
    });
    this.likedRecsController.connect();
    this.recentRecsController.connect();
  }

  /**
   * Só liga/desliga o botão — quem está selecionada só é recalculado na
   * abertura (applyRecsTabSelection), pra um esvaziamento em live só
   * cinzar a aba sem tirar o usuário do que já está vendo.
   * @param {'liked' | 'recent'} key
   * @param {boolean} available
   */
  updateRecsTabAvailability(key, available) {
    this.recsAvailability[key] = available;
    const tab = this.querySelector(`[data-zqcd-recs-tab="${key}"]`);
    if (tab) tab.disabled = !available;
  }

  /** Roda só na abertura — "você pode gostar" tem prioridade se as duas tiverem itens. */
  applyRecsTabSelection() {
    const selected =
      ['liked', 'recent'].find(key => this.recsAvailability[key]) || null;

    this.querySelectorAll('[data-zqcd-recs-tab]').forEach(tab => {
      const isActive = tab.dataset.zqcdRecsTab === selected;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
    });
    this.querySelectorAll('[data-zqcd-recs-panel]').forEach(panel => {
      panel.hidden = panel.dataset.zqcdRecsPanel !== selected;
    });
  }

  handleOpenFromCartIcon = async () => {
    this.open();
    this.cartPanelController.refreshCartPanel();
    await Promise.all([
      this.likedRecsController.load(),
      this.recentRecsController.load(),
    ]);
    this.applyRecsTabSelection();
  };

  handleKeydown = event => {
    if (event.key === 'Escape' && this.isOpen) this.close();
  };

  open = () => {
    this.isOpen = true;
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('zrzs-quick-add-cart-drawer--open');
    this.lockBodyScroll();
  };

  close = () => {
    this.isOpen = false;
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('zrzs-quick-add-cart-drawer--open');
    this.unlockBodyScroll();
  };

  lockBodyScroll() {
    if (document.body.classList.contains('modal-open')) return;
    this.scrollPosition = window.scrollY;
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.classList.add('modal-open');
  }

  unlockBodyScroll() {
    document.body.classList.remove('modal-open');
    document.body.style.top = '';
    if (this.scrollPosition != null) {
      const html = document.documentElement;
      const body = document.body;
      const prevHtmlBehavior = html.style.scrollBehavior;
      const prevBodyBehavior = body.style.scrollBehavior;
      html.style.setProperty('scroll-behavior', 'auto', 'important');
      body.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(0, this.scrollPosition);
      this.scrollPosition = null;
      requestAnimationFrame(() => {
        if (prevHtmlBehavior) html.style.scrollBehavior = prevHtmlBehavior;
        else html.style.removeProperty('scroll-behavior');
        if (prevBodyBehavior) body.style.scrollBehavior = prevBodyBehavior;
        else body.style.removeProperty('scroll-behavior');
      });
    }
  }
}

if (!customElements.get('zrzs-quick-add-cart-drawer')) {
  customElements.define('zrzs-quick-add-cart-drawer', ZRZSQuickAddCartDrawer);
}
